import { apiSlice, ErrorType, errorTransform } from '../../apis/api';
import { getTags } from '../../apis/apiLib';
import { API } from '../../swagger/collections';

const { TAGS, TAGS_ARRAY } = getTags('ybot');

/** What the approval screen shows: who is asking, where the browser goes afterwards, how long the request lives. */
export interface McpAuthorizationRequest {
  clientName: string;
  redirectHost: string;
  expiresAt: string;
}

export interface McpAuthorizationDecision {
  requestId: string;
  approve: boolean;
}

/** Where the frontend sends the browser next; nothing in it is secret (plan decision 6). */
export interface McpAuthorizationContinue {
  continueUrl: string;
}

/**
 * Hand-written calls on purpose: `/Ybot/Mcp/Authorize` are minimal endpoints outside Swagger (a
 * controller POST is audited with its body and needs a project, and this screen has none), so no
 * generated collection exposes them and regenerating the SDK will not "fix" this. They go through the
 * SDK's own axios instance, so the JWT rides along and a failure has the SDK's error shape - the
 * `error as ErrorType` cast below stays valid.
 */
export const apiMcpAuthorize = apiSlice
  .enhanceEndpoints({ addTagTypes: TAGS_ARRAY })
  .injectEndpoints({
    endpoints: builder => ({
      getMcpAuthorizationRequest: builder.query<McpAuthorizationRequest, string>({
        queryFn: async requestId => {
          try {
            const response = await API.instance.get<{ data: McpAuthorizationRequest }>(
              `/Ybot/Mcp/Authorize/${encodeURIComponent(requestId)}`,
            );
            return { data: response.data.data };
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the SDK's axios instance, so the SDK's error shape
            const err = error as ErrorType;
            // errorTransform: the raw AxiosError is not serialisable and Redux warns; the screen only reads response.status.
            return { error: errorTransform(err) };
          }
        },
        providesTags: (_result, _error, requestId) => [
          { type: TAGS.mcpAuthorization, id: requestId },
        ],
      }),
      decideMcpAuthorization: builder.mutation<McpAuthorizationContinue, McpAuthorizationDecision>({
        queryFn: async decision => {
          try {
            const response = await API.instance.post<{ data: McpAuthorizationContinue }>(
              '/Ybot/Mcp/Authorize',
              decision,
            );
            return { data: response.data.data };
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the SDK's axios instance, so the SDK's error shape
            const err = error as ErrorType;
            // errorTransform: the raw AxiosError is not serialisable and Redux warns; the screen only reads response.status.
            return { error: errorTransform(err) };
          }
        },
      }),
    }),
  });

export const { useGetMcpAuthorizationRequestQuery, useDecideMcpAuthorizationMutation } =
  apiMcpAuthorize;
